// Location: src/services/statusWantService.js

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  increment,
  Timestamp,
  documentId,
} from 'firebase/firestore';
import { db, auth } from '../firebase'; // adjust to your firebase init path
import notificationService from './notificationService';

export const StatusWantType = Object.freeze({
  status: 'status',
  want: 'want',
});

/**
 * One ring in the horizontal Status/Want row. A user can have more than
 * one active status or want at once, so this carries every active item
 * as a segment — oldest first, same order the viewer screens play them
 * back in — with a per-segment viewed flag.
 */
export class StatusWantFeedItem {
  constructor({
    type,
    uid,
    name,
    photoUrl,
    latestCreatedAt,
    segments,
    docIds,
  }) {
    this.type = type;
    this.uid = uid;
    this.name = name;
    this.photoUrl = photoUrl ?? null;
    this.latestCreatedAt = latestCreatedAt;
    this.segments = segments;
    this.docIds = docIds;
  }

  /** Whole ring counts as viewed only once every segment has been seen. */
  get isFullyViewed() {
    return this.segments.length > 0 && this.segments.every((v) => v);
  }
}

export class StatusWantFeedResult {
  constructor({ ownStatus, others }) {
    this.ownStatus = ownStatus ?? null;
    this.others = others;
  }

  get isEmpty() {
    return this.ownStatus == null && this.others.length === 0;
  }
}

class StatusWantServiceClass {
  get uid() {
    return auth.currentUser?.uid ?? null;
  }

  collectionFor(type) {
    return type === StatusWantType.status ? 'status' : 'wants';
  }

  createdAtOf(docSnap) {
    const data = docSnap.data();
    return data?.createdAt?.toDate?.() ?? new Date();
  }

  // ── Feed row ─────────────────────────────────────────────────────────

  async loadFeed() {
    const myUid = this.uid;
    const now = Timestamp.now();

    // Single inequality filter, no orderBy — every active doc is grouped
    // and sorted client-side below, so this doesn't need a composite
    // index, and it can't mistake an older, shorter-lived post for the
    // newest one just because Firestore happened to sort by expiresAt.
    const statusSnap = await getDocs(
      query(collection(db, 'status'), where('expiresAt', '>', now))
    );

    const wantSnap = await getDocs(
      query(collection(db, 'wants'), where('expiresAt', '>', now))
    );

    // Every active doc, grouped per uid (not just one "latest" pick).
    const statusByUid = new Map();
    for (const d of statusSnap.docs) {
      const uid = d.data()?.uid?.toString();
      if (!uid) continue;
      if (!statusByUid.has(uid)) statusByUid.set(uid, []);
      statusByUid.get(uid).push(d);
    }

    const wantByUid = new Map();
    for (const d of wantSnap.docs) {
      const uid = d.data()?.uid?.toString();
      if (!uid) continue;
      if (!wantByUid.has(uid)) wantByUid.set(uid, []);
      wantByUid.get(uid).push(d);
    }

    // Oldest → newest per user — matches activeDocsForUser playback order.
    for (const list of statusByUid.values()) {
      list.sort((a, b) => this.createdAtOf(a) - this.createdAtOf(b));
    }
    for (const list of wantByUid.values()) {
      list.sort((a, b) => this.createdAtOf(a) - this.createdAtOf(b));
    }

    const neededUids = new Set([...statusByUid.keys(), ...wantByUid.keys()]);
    const profiles = await this.fetchProfiles(neededUids);

    let ownStatus = null;
    const others = [];

    if (myUid && statusByUid.has(myUid)) {
      ownStatus = await this.buildItem(
        StatusWantType.status,
        statusByUid.get(myUid),
        profiles[myUid],
        myUid
      );
    }

    for (const [uid, docs] of statusByUid.entries()) {
      if (uid === myUid) continue;
      others.push(
        await this.buildItem(StatusWantType.status, docs, profiles[uid], uid)
      );
    }

    for (const [uid, docs] of wantByUid.entries()) {
      others.push(
        await this.buildItem(StatusWantType.want, docs, profiles[uid], uid)
      );
    }

    others.sort((a, b) => {
      if (a.isFullyViewed !== b.isFullyViewed) {
        return a.isFullyViewed ? 1 : -1; // any unseen segment sorts first
      }
      return b.latestCreatedAt - a.latestCreatedAt; // newest first
    });

    return new StatusWantFeedResult({ ownStatus, others });
  }

  async buildItem(type, docs, profile, uid) {
    // One "have I seen this specific post" check per active item, so the
    // ring can show exactly which segments are still fresh.
    const segments = await Promise.all(
      docs.map((d) => this.hasViewed(type, d.id))
    );
    const docIds = docs.map((d) => d.id);

    return new StatusWantFeedItem({
      type,
      uid,
      name:
        profile?.displayName?.toString() ??
        profile?.name?.toString() ??
        'User',
      // Matches user_item.dart's _avatarUrl getter: profileImage first,
      // photoURL as the fallback.
      photoUrl:
        profile?.profileImage && profile.profileImage.toString().length > 0
          ? profile.profileImage.toString()
          : profile?.photoURL?.toString() ?? null,
      latestCreatedAt: this.createdAtOf(docs[docs.length - 1]),
      segments,
      docIds,
    });
  }

  async fetchProfiles(uids) {
    const result = {};
    if (!uids || uids.size === 0) return result;
    const list = Array.from(uids);
    // whereIn caps at 30 values — chunk defensively.
    for (let i = 0; i < list.length; i += 30) {
      const chunk = list.slice(i, i + 30);
      const snap = await getDocs(
        query(collection(db, 'users'), where(documentId(), 'in', chunk))
      );
      for (const d of snap.docs) {
        result[d.id] = d.data();
      }
    }
    return result;
  }

  // ── View tracking ────────────────────────────────────────────────────

  /** Whether the current user has already viewed [docId]. */
  async hasViewed(type, docId) {
    const myUid = this.uid;
    if (!myUid || !docId) return false;
    try {
      const viewSnap = await getDoc(
        doc(db, this.collectionFor(type), docId, 'views', myUid)
      );
      return viewSnap.exists();
    } catch (e) {
      // Never let a failed read here block the feed or the viewer.
      console.warn('[StatusWantService] hasViewed failed', e);
      return false;
    }
  }

  /** Marks [docId] as viewed by the current user. Safe to call repeatedly. */
  async markViewed(type, docId) {
    const myUid = this.uid;
    if (!myUid || !docId) return;
    try {
      await setDoc(doc(db, this.collectionFor(type), docId, 'views', myUid), {
        viewedAt: serverTimestamp(),
        uid: myUid,
      });
    } catch (_) {
      // View tracking is best-effort — never block navigation on it.
    }
  }

  /** Everyone who has viewed [docId], most recently viewed first. */
  async viewersFor(type, docId) {
    if (!docId) return [];
    const snap = await getDocs(
      query(
        collection(db, this.collectionFor(type), docId, 'views'),
        orderBy('viewedAt', 'desc')
      )
    );
    const uids = snap.docs.map((d) => d.id);
    if (uids.length === 0) return [];
    const profiles = await this.fetchProfiles(new Set(uids));
    return uids.map((uid) => ({
      uid,
      displayName:
        profiles[uid]?.displayName ?? profiles[uid]?.name ?? 'User',
      photoUrl:
        profiles[uid]?.profileImage &&
        profiles[uid].profileImage.toString().length > 0
          ? profiles[uid].profileImage
          : profiles[uid]?.photoURL ?? null,
    }));
  }

  // ── Likes ────────────────────────────────────────────────────────────

  /** Whether the current user has already liked [docId]. */
  async hasLiked(type, docId) {
    const myUid = this.uid;
    if (!myUid || !docId) return false;
    try {
      const likeSnap = await getDoc(
        doc(db, this.collectionFor(type), docId, 'likes', myUid)
      );
      return likeSnap.exists();
    } catch (e) {
      console.warn('[StatusWantService] hasLiked failed', e);
      return false;
    }
  }

  /**
   * Toggles the current user's like on [docId] and keeps a `likeCount`
   * field on the parent doc in sync (best-effort).
   * Returns the new liked state (true = now liked).
   */
  async toggleLike(type, docId, { ownerUid, thumbnailUrl } = {}) {
    const myUid = this.uid;
    if (!myUid || !docId) return false;

    const docRef = doc(db, this.collectionFor(type), docId);
    const likeRef = doc(docRef, 'likes', myUid);

    const existing = await getDoc(likeRef);
    if (existing.exists()) {
      await deleteDoc(likeRef);
      updateDoc(docRef, { likeCount: increment(-1) }).catch(() => {});
      return false;
    } else {
      await setDoc(likeRef, {
        uid: myUid,
        likedAt: serverTimestamp(),
      });
      updateDoc(docRef, { likeCount: increment(1) }).catch(() => {});

      if (ownerUid && ownerUid.length > 0 && ownerUid !== myUid) {
        try {
          if (type === StatusWantType.status) {
            notificationService.onStatusLike({
              ownerUid,
              statusId: docId,
              thumbnailUrl,
            });
          } else {
            notificationService.onWantLike({
              ownerUid,
              wantId: docId,
              thumbnailUrl,
            });
          }
        } catch (e) {
          console.warn('[StatusWantService] like notification failed', e);
        }
      }

      return true;
    }
  }

  // ── Shares ───────────────────────────────────────────────────────────

  /** Best-effort share counter — never blocks the share sheet on it. */
  async incrementShareCount(type, docId) {
    if (!docId) return;
    try {
      await updateDoc(doc(db, this.collectionFor(type), docId), {
        shareCount: increment(1),
      });
    } catch (_) {
      // Share tracking is best-effort — never block the share sheet on it.
    }
  }

  // ── Relative time ────────────────────────────────────────────────────

  /** "Just now" / "Xm ago" / "Xh ago" / "Xd ago", falling back to date. */
  static relativeTime(dt) {
    const date = dt instanceof Date ? dt : new Date(dt);
    const diffMs = Date.now() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return 'Just now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHrs = Math.floor(diffMin / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
  }

  // ── Viewer screens ───────────────────────────────────────────────────

  /**
   * All of [uid]'s currently active (non-expired) docs of [type], oldest
   * first — for paging through in the story/detail viewer screens.
   */
  async activeDocsForUser(type, uid) {
    if (!uid) return [];
    const now = Timestamp.now();
    const snap = await getDocs(
      query(
        collection(db, this.collectionFor(type)),
        where('uid', '==', uid),
        where('expiresAt', '>', now)
      )
    );
    const docs = [...snap.docs];
    docs.sort((a, b) => this.createdAtOf(a) - this.createdAtOf(b));
    return docs;
  }
}

// Static helper on the instance too, so `StatusWantService.relativeTime`
// works the way the viewer screen calls it.
StatusWantServiceClass.prototype.relativeTime = StatusWantServiceClass.relativeTime;

export const StatusWantService = new StatusWantServiceClass();
export default StatusWantService;